import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { Search, RotateCcw } from 'lucide-react';
import type { BatchFilter } from '@autopilot/contracts';
import { api } from '../api/client';
import { useCatalogs } from '../api/hooks';
import { PageHeader } from '../components/PageHeader';
import { ErrorAlert, QueryBoundary } from '../components/States';
import { FilterCard } from '../features/batch/FilterCard';
import { defaultFilter, sanitizeStatuses, validateDateRange } from '../lib/filters';
import { useIdempotencyKey } from '../lib/idempotency';

export function NewBatchPage() {
  const catalogs = useCatalogs();
  const nav = useNavigate();
  const qc = useQueryClient();
  const loc = useLocation();
  const initial = (loc.state as { filter?: BatchFilter } | null)?.filter;
  const [filter, setFilter] = useState<BatchFilter>(() =>
    initial ? { ...defaultFilter(), ...initial, statuses: sanitizeStatuses(initial.statuses) } : defaultFilter(),
  );
  const [name, setName] = useState('');
  const idem = useIdempotencyKey();

  const create = useMutation({
    mutationFn: () => {
      const body = { name: name.trim() || undefined, filter };
      return api.createBatch(body, idem.keyFor(body));
    },
    onSuccess: (b) => {
      idem.reset();
      qc.setQueryData(['batch', b.id], b);
      void qc.invalidateQueries({ queryKey: ['batches'] });
      nav(`/batches/${b.id}`);
    },
  });

  const dateError = validateDateRange(filter.createdFromDate, filter.createdToDate);

  return (
    <>
      <PageHeader
        eyebrow="Manueller Batch"
        title="Neuer Batch"
        description="Wählen Sie den Bestand. „Tickets laden“ erstellt eine vollständige, unveränderliche Auswahl – in Nidus wird dabei nichts zugewiesen oder geändert."
      />
      <QueryBoundary query={catalogs} action="Filteroptionen laden" loadingRows={6}>
        {catalogs.data && (
          <Stack spacing={2.5}>
            <FilterCard value={filter} onChange={setFilter} catalogs={catalogs.data} disabled={create.isPending} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <TextField label="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} slotProps={{ htmlInput: { maxLength: 120 } }} sx={{ minWidth: 260, flex: '0 1 360px' }} />
              <Box sx={{ flex: 1 }} />
              <Button startIcon={<RotateCcw size={16} />} onClick={() => setFilter(defaultFilter())} disabled={create.isPending}>
                Filter zurücksetzen
              </Button>
              <Button
                variant="contained"
                size="large"
                startIcon={create.isPending ? <CircularProgress size={16} color="inherit" /> : <Search size={18} />}
                onClick={() => create.mutate()}
                disabled={create.isPending || !!dateError}
                data-testid="load-tickets"
              >
                Tickets laden
              </Button>
            </Box>
            <ErrorAlert error={create.error} action="Tickets laden" onClose={() => create.reset()} />
          </Stack>
        )}
      </QueryBoundary>
    </>
  );
}
